/**
 * Analytics Service
 *
 * Data access layer for analytics and reporting queries.
 * Aggregates data from bookings, inventory, issues, and payment tables.
 *
 * Every function here aggregates over EVERY row matching its filters, so every
 * query must be paged. A single unbounded request stops at the PostgREST
 * `max-rows` cap (1000) and returns the first page with no error, which would
 * silently understate each figure below. Worse, without an ORDER BY the rows
 * that survive are in arbitrary physical order, so the same range could report
 * different totals between two loads. Each query therefore sorts on `id` — a
 * unique key that gives the pages a deterministic boundary — and walks
 * `.range()` windows via `fetchAllPages`.
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import { applyAccessControl, type AccessControl } from '../lib/accessControl';
import { fetchAllPages, fetchAllPagesByChunks, dedupeById } from '../lib/fetchAllPages';

export interface AnalyticsFilters {
  startDate?: string;
  endDate?: string;
  locationId?: string;
  /**
   * Which date column on `bookings` to filter on:
   *   - 'booking' (default): bookings.created_at — when the order was placed
   *   - 'rental': bookings.start_date — when the rental period begins
   * The payment-method breakdown always filters on payment_transactions.created_at
   * regardless of this setting, since it operates on a different table.
   */
  dateBasis?: 'booking' | 'rental';
}

function bookingsDateColumn(filters: AnalyticsFilters | undefined): 'created_at' | 'start_date' {
  return filters?.dateBasis === 'rental' ? 'start_date' : 'created_at';
}

export interface RevenueDataPoint {
  date: string;
  revenue: number;
  collectedRevenue: number;
  /** Money kept on bookings that were cancelled. Included in collectedRevenue. */
  retainedRevenue: number;
  outstandingRevenue: number;
  bookingCount: number;
}

export interface AssetRevenue {
  productTypeId: string;
  productName: string;
  totalRevenue: number;
  collectedRevenue: number;
  outstandingRevenue: number;
  itemCount: number;
  revenuePerAsset: number;
  usageTimes: number;
  usageHours: number;
}

export interface LocationPerformance {
  locationId: string;
  locationName: string;
  totalRevenue: number;
  collectedRevenue: number;
  /** Money kept on bookings that were cancelled. Included in collectedRevenue. */
  retainedRevenue: number;
  outstandingRevenue: number;
  bookingCount: number;
  avgBookingValue: number;
}

export interface ProductIssues {
  productTypeId: string;
  productName: string;
  issueCount: number;
  bySeverity: { low: number; medium: number; high: number; critical: number };
}

export interface BlockingTimeStats {
  reason: string;
  count: number;
  totalDays: number;
}

export interface PaymentChannelStats {
  channel: string;
  totalAmount: number;
  transactionCount: number;
}

export interface BookingLedgerShape {
  total_amount: number | null;
  amount_paid: number | null;
  payment_state: string | null;
  booking_state?: string | null;
  is_test?: boolean | null;
  payment_transactions?: Array<{ amount: number | null; transaction_type?: string | null }> | null;
  refund_history?: Array<{
    refund_amount: number | null;
    scope?: string | null;
    /** See refund_history.restores_balance — absent on legacy selects means false. */
    restores_balance?: boolean | null;
  }> | null;
}

/**
 * Gross booking payments as recorded: the larger of `bookings.amount_paid` and
 * the sum of the booking's `payment_transactions`, damage charges excluded.
 *
 * Both only ever grow with real payments (neither is reduced by refunds, and
 * damage charges touch neither), so each is a lower bound and the larger one is
 * the better figure. The ledger is short where a payment never got a row — a
 * booking predating the ledger, or one marked paid at admin creation whose
 * ledger insert failed (it is non-fatal) and which later took a balance
 * payment, leaving only that balance in the ledger. amount_paid is short where
 * a payment path did not update it (e.g. some PayPal payments). The
 * manage-booking app uses the same rule, so both show the customer the same
 * outstanding amount.
 */
export function deriveRecordedBookingPaid(
  amountPaid: number | null | undefined,
  transactions: Array<{ amount: number | null; transaction_type?: string | null }> | null | undefined,
): number {
  const ledgerPaid = Array.isArray(transactions)
    ? transactions
        .filter(tx => tx.transaction_type !== 'damage_charge')
        .reduce((sum, tx) => sum + (tx.amount || 0), 0)
    : 0;
  return Math.max(amountPaid || 0, ledgerPaid);
}

/**
 * Gross booking payments — everything the customer actually paid towards the
 * rental, before refunds and excluding the damage ledger.
 */
function deriveGrossBookingPaid(booking: BookingLedgerShape): number {
  const paid = deriveRecordedBookingPaid(booking.amount_paid, booking.payment_transactions);
  if (booking.payment_state === 'paid' && paid < 0.01) {
    // Legacy fallback: marked paid but no payment recorded anywhere, trust payment_state
    return booking.total_amount || 0;
  }
  return paid;
}

/** Booking-scope refunds — the damage ledger is excluded, see below. */
function deriveBookingRefunded(booking: BookingLedgerShape): number {
  const refunds = booking.refund_history;
  return Array.isArray(refunds)
    ? refunds.reduce(
        (sum, r) => (r.scope === 'damage' ? sum : sum + Number(r.refund_amount || 0)),
        0,
      )
    : 0;
}

/**
 * Booking-scope refunds that returned money for items removed from the booking
 * or for an overpayment (refund_history.restores_balance). The total is or will
 * be reduced separately for those, so the refund must be added back when
 * measuring what the customer still owes — otherwise it acts as a credit
 * against anything added to the booking later. Cancellation and goodwill
 * refunds carry FALSE and are not included.
 */
function deriveBalanceRestoringRefunded(booking: BookingLedgerShape): number {
  const refunds = booking.refund_history;
  return Array.isArray(refunds)
    ? refunds.reduce(
        (sum, r) =>
          r.scope !== 'damage' && r.restores_balance === true ? sum + Number(r.refund_amount || 0) : sum,
        0,
      )
    : 0;
}

/**
 * Derive the net collected (paid) amount for a booking.
 *
 * Gross paid is the larger of `payment_transactions` (one row per successful
 * payment across Stripe, PayPal, manual, etc.) and `bookings.amount_paid` — see
 * deriveRecordedBookingPaid. Refunds from
 * `refund_history` are subtracted regardless of who processed them — Stripe,
 * PayPal, or bookkeeping-only — because an operator should see money that
 * actually left the business, whoever pushed the button. This produces a single,
 * consistent "net collected" value so every surface (Analytics tiles, Dashboard
 * outstanding, BookingFinancialView) agrees on how much money the business has
 * actually kept for a given booking.
 *
 * The damage-charge ledger is excluded from both halves. Off-session damage
 * charges (`transaction_type = 'damage_charge'`) are compensation for damage,
 * late return or cleaning — not rental revenue — and never moved
 * bookings.total_amount, so counting them here would inflate revenue against an
 * unchanged total. Their refunds (`scope = 'damage'`) are excluded symmetrically:
 * subtracting a reversal of money that was never counted would understate it.
 * `scope = 'unknown'` refunds stay subtracted — they are a closed set of legacy
 * rows that could not be classified, and the conservative reading is that the
 * money went back.
 *
 * Note this is deliberately NOT the same basis as percentage platform-fee
 * invoicing, which additionally excludes bookkeeping-only and pending-review
 * refunds and attributes each payment to the month it was made rather than to
 * its booking. The two figures answer different questions and will not
 * reconcile month to month.
 *
 * Always returns ≥ 0.
 */
export function deriveCollectedAmount(booking: BookingLedgerShape): number {
  if (booking.payment_state === 'refunded') return 0;
  return Math.max(0, deriveGrossBookingPaid(booking) - deriveBookingRefunded(booking));
}

/**
 * Derive what the customer still OWES on a booking.
 *
 * Measured against GROSS payments, not payments net of refunds, because a refund
 * reduces the obligation as well as the collection — it does not turn refunded
 * money back into a receivable. A booking paid in full and then 90% refunded is
 * settled: the customer owes nothing, and the merchant kept the remaining 10%.
 * Computing this as `total - (paid - refunded)` instead would report the refund
 * itself as outstanding, and every surface built on it would invite the operator
 * to chase money that was deliberately given back.
 *
 * Deliberately does NOT short-circuit on payment_state 'refunded' the way
 * deriveCollectedAmount does: a fully refunded booking has collected nothing, but
 * it is also owed nothing.
 *
 * Re-collecting after a refund issued in error is a real workflow, but it is the
 * rarer case and it is not expressed by making the refund look like a debt — the
 * UI keeps a payment action available whenever refunds exist.
 *
 * The one exception is a refund that returned money for something taken OFF the
 * booking (BK-2026-007218: €10 battery light refunded and removed, then a day
 * added). That refund did not reduce the obligation — the item removal did — so
 * measuring against gross payments alone would credit it against the new day.
 * Such refunds are flagged `restores_balance` by the operator at refund time and
 * added back here. Every refund without the flag behaves exactly as described
 * above.
 */
export function deriveOutstandingAmount(booking: BookingLedgerShape): number {
  return Math.max(
    0,
    (booking.total_amount || 0) - deriveGrossBookingPaid(booking) + deriveBalanceRestoringRefunded(booking),
  );
}

/**
 * Money the merchant kept on a booking that was cancelled.
 *
 * Cancelled bookings used to be excluded from analytics outright
 * (`.neq('booking_state', 'cancelled')`), which reported this as zero. It is
 * not zero: 152 cancelled bookings platform-wide still hold EUR 8,806, and only
 * a small part of that is deliberate cancellation fees. The rest is money kept
 * because the merchant delivered the service another way — islecycle
 * BK-2026-002340 is EUR 373.62 where the customer was given bikes on a
 * different day instead of a refund — or applied a policy without refunding.
 *
 * Hiding it is not merely incomplete, it actively contradicts the invoice:
 * percentage platform-fee billing reads payment_transactions, which has no
 * booking_state filter, so a merchant is charged a fee on revenue their own
 * dashboard says they never earned.
 *
 * Requires positive evidence of payment. deriveGrossBookingPaid falls back to
 * total_amount for a legacy booking marked paid with no recorded amount, which
 * is a reasonable guess for a live booking and a bad one here — it would invent
 * retained revenue on a cancelled booking nobody ever paid for.
 */
export function deriveRetainedAmount(booking: BookingLedgerShape): number {
  if (booking.booking_state !== 'cancelled') return 0;
  const hasLedgerRows =
    Array.isArray(booking.payment_transactions) && booking.payment_transactions.length > 0;
  const hasRecordedPayment = (booking.amount_paid || 0) > 0;
  if (!hasLedgerRows && !hasRecordedPayment) return 0;
  return deriveCollectedAmount(booking);
}

export interface BookingContribution {
  /** Order value. A cancelled rental did not happen, so it contributes nothing. */
  bookedRevenue: number;
  /** Net money taken and kept, cancelled bookings included. */
  collectedRevenue: number;
  /** The cancelled-booking part of collectedRevenue, so it can be labelled. */
  retainedRevenue: number;
  /** Nobody owes anything on a cancelled booking. */
  outstandingRevenue: number;
  /** Whether this booking counts towards booking volume. */
  countsAsBooking: boolean;
}

/**
 * How one booking contributes to each analytics figure.
 *
 * Single definition on purpose: every surface that groups bookings — the
 * revenue chart, the location table, and anything added later — must split
 * cancelled bookings the same way, or two panels over the same date range
 * disagree. Divergent copies of one calculation is the failure mode that
 * produced both the VAT and the outstanding-amount bugs.
 *
 * The split: a cancelled booking contributes its retained money and nothing
 * else. It is not booked revenue (the rental did not happen), not outstanding
 * (nobody owes anything on it), and not a booking (it would drag every
 * per-booking average towards zero).
 */
export function deriveBookingContribution(booking: BookingLedgerShape): BookingContribution {
  // A test booking contributes nothing anywhere. The aggregate queries also
  // filter it out for efficiency; this guard is what makes the exclusion a
  // property of the calculation rather than of remembering to add a clause,
  // so a future caller cannot reintroduce test bookings by accident.
  if (booking.is_test) {
    return {
      bookedRevenue: 0,
      collectedRevenue: 0,
      retainedRevenue: 0,
      outstandingRevenue: 0,
      countsAsBooking: false,
    };
  }

  if (booking.booking_state === 'cancelled') {
    const retained = deriveRetainedAmount(booking);
    return {
      bookedRevenue: 0,
      collectedRevenue: retained,
      retainedRevenue: retained,
      outstandingRevenue: 0,
      countsAsBooking: false,
    };
  }

  return {
    bookedRevenue: booking.total_amount || 0,
    collectedRevenue: deriveCollectedAmount(booking),
    retainedRevenue: 0,
    outstandingRevenue: deriveOutstandingAmount(booking),
    countsAsBooking: true,
  };
}

/**
 * Get revenue data points over a date range, grouped by day/week/month.
 */
export async function getRevenueOverTime(
  accessControl: AccessControl | null,
  filters: AnalyticsFilters
) {
  try {
    const dateColumn = bookingsDateColumn(filters);
    // Cancelled bookings are deliberately NOT filtered out here. They are split
    // by deriveBookingContribution instead: their retained money counts, their
    // order value and booking count do not. Excluding them entirely reported
    // EUR 8,806 of real revenue as zero — see deriveRetainedAmount. Dropping
    // that filter widens the result set, so the paging below matters more here
    // than it did before, not less.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select(`id, total_amount, amount_paid, payment_state, created_at, start_date, location_id, booking_state, is_test, locations!inner(merchant_id), payment_transactions(amount, transaction_type), refund_history(refund_amount, scope, restores_balance)`)
        .eq('is_test', false)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id',
      });

      if (filters.locationId) {
        query = query.eq('location_id', filters.locationId);
      }
      if (filters.startDate) {
        query = query.gte(dateColumn, filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte(dateColumn, filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Group by date, tracking booked vs collected revenue
    const grouped: Record<string, { revenue: number; collectedRevenue: number; retainedRevenue: number; outstandingRevenue: number; bookingCount: number }> = {};
    (data || []).forEach((b: any) => {
      const date = (b[dateColumn] || '').split('T')[0];
      if (!date) return;
      if (!grouped[date]) grouped[date] = { revenue: 0, collectedRevenue: 0, retainedRevenue: 0, outstandingRevenue: 0, bookingCount: 0 };
      const c = deriveBookingContribution(b);
      grouped[date].revenue += c.bookedRevenue;
      grouped[date].collectedRevenue += c.collectedRevenue;
      grouped[date].retainedRevenue += c.retainedRevenue;
      grouped[date].outstandingRevenue += c.outstandingRevenue;
      grouped[date].bookingCount += c.countsAsBooking ? 1 : 0;
    });

    const result: RevenueDataPoint[] = Object.entries(grouped)
      .map(([date, vals]) => ({
        date,
        revenue: vals.revenue,
        collectedRevenue: vals.collectedRevenue,
        retainedRevenue: vals.retainedRevenue,
        outstandingRevenue: vals.outstandingRevenue,
        bookingCount: vals.bookingCount,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get revenue per asset (product type), computed from booking_items.
 * Queries from bookings (proven pattern) and flattens booking_items client-side.
 */
export async function getRevenuePerAsset(
  accessControl: AccessControl | null,
  filters?: AnalyticsFilters
) {
  try {
    // Query from bookings (same proven pattern as other analytics functions)
    // and include booking_items with their product type info
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select(`
        id,
        booking_state,
        total_amount,
        amount_paid,
        payment_state,
        location_id,
        locations!inner(merchant_id),
        payment_transactions(amount, transaction_type),
        refund_history(refund_amount, scope, restores_balance),
        booking_items(
          product_type_id,
          total_price,
          quantity,
          duration_hours,
          item_name
        )
      `)
        .neq('booking_state', 'cancelled')
        .eq('is_test', false)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id',
      });

      if (filters?.locationId) {
        query = query.eq('location_id', filters.locationId);
      }
      const assetDateColumn = bookingsDateColumn(filters);
      if (filters?.startDate) {
        query = query.gte(assetDateColumn, filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte(assetDateColumn, filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Flatten booking_items from all bookings and group by product_type_id
    // Distribute booking-level collected & outstanding proportionally across items
    const grouped: Record<string, { name: string; revenue: number; collected: number; outstanding: number; rentals: number; hours: number }> = {};
    (data || []).forEach((booking: any) => {
      const items = Array.isArray(booking.booking_items) ? booking.booking_items : [];
      const bookingCollected = deriveCollectedAmount(booking);
      const bookingOutstanding = deriveOutstandingAmount(booking);
      // Sum of item prices (may differ from booking total due to discounts/fees)
      const itemsTotalPrice = items.reduce((s: number, i: any) => s + (i.total_price || 0), 0);

      items.forEach((item: any) => {
        const ptId = item.product_type_id;
        if (!ptId) return;
        if (!grouped[ptId]) {
          grouped[ptId] = { name: item.item_name || ptId, revenue: 0, collected: 0, outstanding: 0, rentals: 0, hours: 0 };
        }
        const itemPrice = item.total_price || 0;
        // Each item's share of the booking, used to distribute booking-level amounts
        const itemShare = itemsTotalPrice > 0 ? itemPrice / itemsTotalPrice : 0;
        grouped[ptId].revenue += itemPrice;
        grouped[ptId].collected += bookingCollected * itemShare;
        grouped[ptId].outstanding += bookingOutstanding * itemShare;
        grouped[ptId].rentals += item.quantity || 1;
        grouped[ptId].hours += item.duration_hours || 0;
      });
    });

    const productTypeIds = Object.keys(grouped);
    if (productTypeIds.length === 0) {
      return { data: [], error: null };
    }

    // Fetch inventory items with access control (RLS requires the join chain)
    // to count items per product type. Both dimensions are unbounded here — the
    // product-type list is as long as the merchant's catalogue and the item
    // count is the whole fleet — so the `.in()` list is chunked and each chunk
    // paged. Undercounting here would not just drop rows: itemCount is the
    // DIVISOR of revenuePerAsset, so a truncated fleet inflates the headline
    // number rather than shrinking it.
    const invItems = dedupeById(await fetchAllPagesByChunks<any, string>(
      productTypeIds,
      (ids, from, to) => {
        let invQuery = supabase
          .from('inventory_items')
          .select('id, product_type_id, product_types!inventory_items_product_type_id_fkey!inner(id, product_name, locations!inner(merchant_id))')
          .in('product_type_id', ids)
          .order('id', { ascending: true });

        invQuery = applyAccessControl(invQuery, accessControl, {
          merchantIdColumn: 'product_types.locations.merchant_id',
        });

        return invQuery.range(from, to);
      }
    ));

    // Build product name + inventory count map
    const productMap: Record<string, { name: string; itemCount: number }> = {};
    (invItems || []).forEach((inv: any) => {
      const ptId = inv.product_type_id;
      if (!ptId) return;
      if (!productMap[ptId]) {
        productMap[ptId] = {
          name: inv.product_types?.product_name || grouped[ptId]?.name || ptId,
          itemCount: 0,
        };
      }
      productMap[ptId].itemCount += 1;
    });

    const result: AssetRevenue[] = productTypeIds
      .map((ptId) => {
        const stats = grouped[ptId];
        const product = productMap[ptId];
        const name = product?.name || stats.name;
        const itemCount = product?.itemCount || 0;
        const collectedRevenue = stats.collected;
        return {
          productTypeId: ptId,
          productName: name,
          totalRevenue: stats.revenue,
          collectedRevenue,
          outstandingRevenue: stats.outstanding,
          itemCount,
          revenuePerAsset: itemCount > 0 ? collectedRevenue / itemCount : collectedRevenue,
          usageTimes: stats.rentals,
          usageHours: stats.hours,
        };
      })
      .sort((a, b) => b.revenuePerAsset - a.revenuePerAsset);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get performance metrics per location.
 */
export async function getLocationPerformance(
  accessControl: AccessControl | null,
  filters: AnalyticsFilters
) {
  try {
    // Same treatment as getRevenueOverTime — cancelled bookings are split, not
    // dropped, so this table and the revenue chart cannot disagree.
    // `id` is selected purely as the paging sort key and dedupe key; the
    // aggregation below does not read it.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select('id, location_id, total_amount, amount_paid, payment_state, booking_state, is_test, locations!inner(merchant_id, location_name), payment_transactions(amount, transaction_type), refund_history(refund_amount, scope, restores_balance)')
        .eq('is_test', false)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id',
      });

      const locDateColumn = bookingsDateColumn(filters);
      if (filters.startDate) {
        query = query.gte(locDateColumn, filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte(locDateColumn, filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Group by location
    const grouped: Record<string, { locationName: string; revenue: number; collected: number; retained: number; outstanding: number; count: number }> = {};
    (data || []).forEach((b: any) => {
      const locId = b.location_id;
      const locName = (b.locations as any)?.location_name || 'Unknown';
      if (!grouped[locId]) grouped[locId] = { locationName: locName, revenue: 0, collected: 0, retained: 0, outstanding: 0, count: 0 };
      const c = deriveBookingContribution(b);
      grouped[locId].revenue += c.bookedRevenue;
      grouped[locId].collected += c.collectedRevenue;
      grouped[locId].retained += c.retainedRevenue;
      grouped[locId].outstanding += c.outstandingRevenue;
      grouped[locId].count += c.countsAsBooking ? 1 : 0;
    });

    const result: LocationPerformance[] = Object.entries(grouped)
      .map(([locationId, vals]) => ({
        locationId,
        locationName: vals.locationName,
        totalRevenue: vals.revenue,
        collectedRevenue: vals.collected,
        retainedRevenue: vals.retained,
        outstandingRevenue: vals.outstanding,
        bookingCount: vals.count,
        avgBookingValue: vals.count > 0 ? vals.collected / vals.count : 0,
      }))
      .sort((a, b) => b.collectedRevenue - a.collectedRevenue);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get open issues grouped by product type.
 */
export async function getIssuesByProduct(
  accessControl: AccessControl | null
) {
  try {
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('issues')
        .select('id, severity, status, product_type_id, merchant_id, location_id')
        .in('status', ['reported', 'confirmed', 'repair_assigned'])
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl);

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // We need product names - fetch them separately
    const productTypeIds = [...new Set((data || []).map((i: any) => i.product_type_id).filter((id: any): id is string => id !== null))];

    let productNames: Record<string, string> = {};
    const products = await fetchAllPagesByChunks<any, string>(
      productTypeIds,
      (ids, from, to) =>
        supabase
          .from('product_types')
          .select('id, product_name')
          .in('id', ids)
          .order('id', { ascending: true })
          .range(from, to)
    );
    products.forEach((pt: any) => {
      productNames[pt.id] = pt.product_name;
    });

    // Group by product type
    const grouped: Record<string, { count: number; severity: Record<string, number> }> = {};
    (data || []).forEach((issue: any) => {
      const ptId = issue.product_type_id || 'unassigned';
      if (!grouped[ptId]) grouped[ptId] = { count: 0, severity: {} };
      grouped[ptId].count += 1;
      const sev = (issue.severity || 'medium').toLowerCase();
      grouped[ptId].severity[sev] = (grouped[ptId].severity[sev] || 0) + 1;
    });

    const result: ProductIssues[] = Object.entries(grouped)
      .map(([ptId, vals]) => ({
        productTypeId: ptId,
        productName: productNames[ptId] || 'Unassigned',
        issueCount: vals.count,
        bySeverity: {
          low: vals.severity['low'] || 0,
          medium: vals.severity['medium'] || 0,
          high: vals.severity['high'] || 0,
          critical: vals.severity['critical'] || 0,
        },
      }))
      .sort((a, b) => b.issueCount - a.issueCount);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get blocking time statistics grouped by reason.
 */
export async function getBlockingTimeStats(
  accessControl: AccessControl | null,
  filters?: AnalyticsFilters
) {
  try {
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('inventory_blocking_times')
        .select(`
        id,
        blocking_reason,
        blocked_from,
        blocked_until,
        inventory_item_id,
        inventory_items!inner(
          product_type_id,
          product_types!inner(
            location_id,
            locations!inner(merchant_id)
          )
        )
      `)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'inventory_items.product_types.locations.merchant_id',
        locationIdColumn: 'inventory_items.product_types.location_id',
      });

      if (filters?.startDate) {
        query = query.gte('blocked_from', filters.startDate);
      }
      if (filters?.endDate) {
        query = query.lte('blocked_from', filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    // Group by reason
    const grouped: Record<string, { count: number; totalDays: number }> = {};
    (data || []).forEach((bt: any) => {
      const reason = bt.blocking_reason || 'Other';
      if (!grouped[reason]) grouped[reason] = { count: 0, totalDays: 0 };
      grouped[reason].count += 1;

      const from = new Date(bt.blocked_from);
      const until = new Date(bt.blocked_until);
      const days = Math.max(1, Math.ceil((until.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)));
      grouped[reason].totalDays += days;
    });

    const result: BlockingTimeStats[] = Object.entries(grouped)
      .map(([reason, vals]) => ({ reason, count: vals.count, totalDays: vals.totalDays }))
      .sort((a, b) => b.count - a.count);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get booking state breakdown for a date range.
 */
export async function getBookingStatusBreakdown(
  accessControl: AccessControl | null,
  filters: AnalyticsFilters
) {
  try {
    // `id` is selected purely as the paging sort key and dedupe key.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('bookings')
        .select('id, booking_state, location_id, locations!inner(merchant_id)')
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl, {
        merchantIdColumn: 'locations.merchant_id',
        locationIdColumn: 'location_id',
      });

      if (filters.locationId) {
        query = query.eq('location_id', filters.locationId);
      }
      const statusDateColumn = bookingsDateColumn(filters);
      if (filters.startDate) {
        query = query.gte(statusDateColumn, filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte(statusDateColumn, filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    const grouped: Record<string, number> = {};
    (data || []).forEach((b: any) => {
      const state = b.booking_state || 'unknown';
      grouped[state] = (grouped[state] || 0) + 1;
    });

    const result = Object.entries(grouped)
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}

/**
 * Get payment method breakdown.
 */
export async function getPaymentMethodBreakdown(
  accessControl: AccessControl | null,
  filters: AnalyticsFilters
) {
  try {
    // This is the first analytics query to reach the row cap in practice: a
    // booking contributes at least one transaction and split payments add more,
    // so the transaction ledger outgrows the booking table it is derived from.
    // `id` is selected purely as the paging sort key and dedupe key.
    const buildPage = (from: number, to: number) => {
      let query = supabase
        .from('payment_transactions')
        // The inner join to bookings exists solely to reach is_test. This panel
        // reads the ledger directly, so without it a test booking's real card
        // payment still shows up here as ordinary revenue while every other
        // surface excludes it. It is an inner join, so it also drops any
        // transaction whose booking has been deleted — none exist, and the
        // delete guard prevents new ones.
        .select('id, channel, amount, merchant_id, bookings!inner(is_test)')
        // Booking payments only — off-session damage charges always carry
        // channel 'stripe_card' and would otherwise show up here as ordinary card
        // revenue. Same exclusion as deriveCollectedAmount, for the same reason.
        .eq('transaction_type', 'booking_payment')
        .eq('bookings.is_test', false)
        .order('id', { ascending: true });

      query = applyAccessControl(query, accessControl);

      if (filters.startDate) {
        query = query.gte('created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('created_at', filters.endDate);
      }

      return query.range(from, to);
    };

    const data = dedupeById(await fetchAllPages<any>(buildPage));

    const grouped: Record<string, { totalAmount: number; count: number }> = {};
    (data || []).forEach((t: any) => {
      const ch = t.channel || 'unknown';
      if (!grouped[ch]) grouped[ch] = { totalAmount: 0, count: 0 };
      grouped[ch].totalAmount += t.amount || 0;
      grouped[ch].count += 1;
    });

    const result: PaymentChannelStats[] = Object.entries(grouped)
      .map(([channel, vals]) => ({
        channel,
        totalAmount: vals.totalAmount,
        transactionCount: vals.count,
      }))
      .sort((a, b) => b.totalAmount - a.totalAmount);

    return { data: result, error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
